import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AxiosRequestConfig } from 'axios';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import { Mixpanel } from 'mixpanel';

import logo from '/logo.svg';
import hiddenIcon from '/hiddenPassword.svg';
import unhiddenIcon from '/unhiddenPassword.svg';

import Button from 'components/Button';
import Input from 'components/Input';
import loginMutation from 'api/mutations/loginMutation';
import { queryClient } from 'utils/queryClient';
import Loading from 'components/Loading';
import { agent } from 'api/agent';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from 'components/Form';
import SecureStorage from 'utils/localStorage';
import { isOmya } from 'utils/isOmya';

import { signInFormSchema } from './schema';

const Login = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { mutate, isPending } = loginMutation();
  const [hidden, setHidden] = useState<boolean>(true);
  const form = useForm<z.infer<typeof signInFormSchema>>({
    resolver: zodResolver(signInFormSchema),
  });

  const {
    handleSubmit,
    control,
    getValues,
  } = form;

  const onSubmit = handleSubmit((data: z.infer<typeof signInFormSchema>) => {
    mutate(
      {
        password: data.password,
        email: data.email,
      },
      {
        onSuccess(res) {
          if (isOmya(res.data?.user?.organization)) {
            Mixpanel.identify(res.data?.user?.email);
            Mixpanel.track('Sign In', {
              email: res.data?.user?.email,
              role: res.data?.user?.role?.name,
              organization: res.data?.user?.organization ?? 'No organization',
            });
          } else {
            Mixpanel.identify();
            Mixpanel.track('Sign In', {
              role: res.data?.user?.role?.name,
              organization: res.data?.user?.organization ?? 'No organization',
            });
          }

          if (res.data?.user) {
            queryClient.setQueryData(['user'], res.data?.user);
          }

          SecureStorage.setItem('token', res.data.token, { secure: true });
          SecureStorage.setItem('refreshToken', res.data.refreshToken, { secure: true });
          agent.getClient(false).defaults.headers.common[
            'Authorization'
          ] = `Bearer ${res.data.token}`;

          switch (true) {
            case !res.data?.user?.phoneNumber: {
              navigate('/auth/set-phone-number');

              break;
            }
            case res?.data?.user?.isTwoFAEnabled: {
              navigate('/auth/confirm-mfa');

              break;
            }
            default: {
              navigate('/');
            }
          }
        },
        onError(err: any) {
          if (err?.response?.data?.errors?.email) {
            form.setError('email', { message: err?.response?.data?.errors?.email });
          }

          if (err?.response?.data?.errors?.password) {
            form.setError('password', { message: err?.response?.data?.errors?.password });
          }
        },
      },
    );
  });

  return (
    <div className="flex flex-col w-full items-center justify-center max-h-screen">
      <div className="flex flex-col border py-[60px] px-[40px] rounded-2xl border-[#CBCBCB] w-[480px] bg-[#fff] md:w-[358px] md:py-[40px] md:mb-[130px]">
        <div className="justify-center items-center flex">
          <img src={logo} alt="logo" />
        </div>
        <div className="text-3xl font-bold my-[40px] justify-center items-center flex md:text-2xl md:my-[20px]">
          {t('welcomeToEvs')}
        </div>
        <Form {...form}>
          <form onSubmit={onSubmit}>
            <div className="relative">
              <FormField
                control={control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        className={`w-11/12 m-4 ${
                          field.value?.length > 0 ? 'bg-primitiveBlue' : 'bg-white'
                        }`}
                        placeholder="email@domain.com"
                        title={`${t('email')}*`}
                        titleClassName={`${
                          field.value?.length > 0 ? 'bg-blue-gradient' : 'bg-white'
                        }  mt-0.5`}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage className='ml-4 pb-2'/>
                  </FormItem>
                )}
              />

            </div>
            <div className="relative">
              <FormField
                control={control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        className={`w-11/12 m-4 ${
                          field.value?.length > 0 ? 'bg-primitiveBlue' : 'bg-white'
                        }`}
                        placeholder="•••••••"
                        title={`${t('password')}*`}
                        type={hidden ? 'password' : 'text'}
                        rightIcon={hidden ? hiddenIcon : unhiddenIcon}
                        onRightIconClick={() => setHidden(!hidden)}
                        arrowRightClassName="top-7 right-6"
                        titleClassName={`${
                          field.value?.length > 0 ? 'bg-blue-gradient' : 'bg-white'
                        }  mt-0.5`}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage className='ml-4 pb-2'/>
                  </FormItem>
                )}
              />
            </div>
            <button
              type='button'
              className="flex justify-end items-end text-[#171717] ml-auto mr-4 text-sm cursor-pointer"
              onClick={() => {
                const email = getValues().email;

                if (!email) {
                  return toast('Enter email first', {
                    type: 'error',
                    position: 'top-center',
                  });
                }

                const config: AxiosRequestConfig = {
                  method: 'post',
                  data: { email },
                };
                agent.getClient()('auth/forgot/password', config)
                  .then(() => {
                    toast('The notification was sent to email', {
                      type: 'success',
                      position: 'top-center',
                    });
                  });
              }}
            >
              Forgot password?
            </button>
            <div className="flex justify-center mt-5">
              <Button
                disabled={isPending}
                type="submit"
                btnType="primary">
                {isPending ? <Loading size={25}/> : t('signIn')}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};

export default Login;
